import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

export type EventArchiveCommand = "ARCHIVE_EVENT" | "RESTORE_EVENT";

/** How long to wait for the backend to settle a request before offering a retry. */
export const ARCHIVE_TIMEOUT_MS = 30_000;
export const ARCHIVE_TIMEOUT_MESSAGE =
  "This is taking longer than expected. You can try again.";

const archiveRequestSchema = z.object({
  superseded: z.boolean().optional(),
  status: z.enum(["REQUESTED", "IN_PROGRESS", "SUCCEEDED", "FAILED"]),
  error: z.string().optional(),
});

export type EventArchiveState =
  | { status: "idle" }
  | { status: "submitting"; command: EventArchiveCommand }
  | { status: "watching"; command: EventArchiveCommand }
  | { status: "succeeded"; command: EventArchiveCommand; superseded: boolean }
  | { status: "failed"; error: string };

/**
 * What one snapshot of `event_archive_requests/{id}` means for the admin, or
 * null for a document that does not parse or has not settled. A request that
 * ends SUCCEEDED as superseded is a success: the event is already in the
 * state of a later request.
 */
export function archiveOutcomeFromRequest(
  data: unknown,
  command: EventArchiveCommand,
): EventArchiveState | null {
  const parsed = archiveRequestSchema.safeParse(data);
  if (!parsed.success) return null;
  if (parsed.data.status === "SUCCEEDED") {
    return {
      status: "succeeded",
      command,
      superseded: parsed.data.superseded === true,
    };
  }
  if (parsed.data.status === "FAILED") {
    return {
      status: "failed",
      error: parsed.data.error ?? "The event could not be updated.",
    };
  }
  return null;
}

export function eventArchiveRequestDocument(
  command: EventArchiveCommand,
  eventId: string,
  uid: string,
  requestedAt: unknown,
) {
  return {
    type: command,
    eventId,
    requestedBy: uid,
    requestedAt,
    status: "REQUESTED",
  } as const;
}

/**
 * Files archive and restore requests and watches the one in flight. The
 * event's own archived state is the caller's: it comes from the page's
 * settings read, and `onSucceeded` is where the page reloads it. No listener
 * stays on the event document, because a reload that reads a document with
 * an active listener can return the state from before a transaction.
 */
export function useEventArchive(
  eventId: string,
  uid: string,
  onSucceeded?: () => void,
) {
  const [state, setState] = useState<EventArchiveState>({ status: "idle" });
  const stopWatching = useRef<(() => void) | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filing = useRef(false);
  const mounted = useRef(true);
  const onSucceededRef = useRef(onSucceeded);
  useEffect(() => {
    onSucceededRef.current = onSucceeded;
  });

  const cleanup = useCallback(() => {
    stopWatching.current?.();
    stopWatching.current = null;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cleanup();
    };
  }, [cleanup]);

  const submit = useCallback(
    async (command: EventArchiveCommand) => {
      // A second click while one request is in flight must not file another.
      if (filing.current) return;
      filing.current = true;
      cleanup();
      setState({ status: "submitting", command });
      const finish = (next: EventArchiveState) => {
        cleanup();
        filing.current = false;
        setState(next);
        if (next.status === "succeeded") onSucceededRef.current?.();
      };
      try {
        const request = await addDoc(
          collection(db, "event_archive_requests"),
          eventArchiveRequestDocument(command, eventId, uid, serverTimestamp()),
        );
        // Unmounted while the write was in flight: nothing may be left
        // listening or counting down.
        if (!mounted.current) {
          filing.current = false;
          return;
        }
        setState({ status: "watching", command });
        timer.current = setTimeout(
          () => finish({ status: "failed", error: ARCHIVE_TIMEOUT_MESSAGE }),
          ARCHIVE_TIMEOUT_MS,
        );
        stopWatching.current = onSnapshot(
          doc(db, "event_archive_requests", request.id),
          (snapshot) => {
            if (!snapshot.exists()) return;
            const next = archiveOutcomeFromRequest(snapshot.data(), command);
            if (next !== null) finish(next);
          },
          (error) => finish({ status: "failed", error: error.message }),
        );
      } catch (error) {
        finish({
          status: "failed",
          error:
            error instanceof Error
              ? error.message
              : "Could not request the change",
        });
      }
    },
    [cleanup, eventId, uid],
  );

  const reset = useCallback(() => {
    cleanup();
    filing.current = false;
    setState({ status: "idle" });
  }, [cleanup]);

  const pending = state.status === "submitting" || state.status === "watching";
  return { state, pending, submit, reset };
}
